// Fix II (2026-10-30): el depósito en obra agrupado por origen (Stock CEN, Proveedor externo, Caja Chica) con consumo y
// saldo; lo que llega a cero se oculta y queda en el «Historial de consumo». Caja Chica entra al confirmar su entrega.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ClipboardList, History, PackageCheck, Warehouse } from "lucide-react";
import { API } from "../../contexts/AuthContext";

const opts = { withCredentials: true };
const err = (e, def) => e?.response?.data?.detail || def;
const num = (v) => Number(v || 0).toLocaleString("es-PY", { maximumFractionDigits: 4 });
const hoyPY = () => new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);
const fechaHora = (iso) => {
  try { return new Date(iso).toLocaleString("es-PY", { timeZone: "America/Asuncion", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso || ""; }
};

export default function DepositoObraPanel({ depositoId, onChange }) {
  const [data, setData] = useState(null);
  const [consumo, setConsumo] = useState({});
  const [fecha, setFecha] = useState(hoyPY());
  const [notas, setNotas] = useState("");
  const [busy, setBusy] = useState("");
  const [entregas, setEntregas] = useState({});
  const [historial, setHistorial] = useState(null);
  const [verHistorial, setVerHistorial] = useState(false);

  const cargar = useCallback(async () => {
    if (!depositoId) { setData(null); return; }
    try {
      const { data: d } = await axios.get(`${API}/inventory/depositos/${encodeURIComponent(depositoId)}/existencias`, opts);
      setData(d); setConsumo({}); setEntregas({});
    } catch (e) { toast.error(err(e, "No se pudo cargar el depósito")); }
  }, [depositoId]);
  useEffect(() => { cargar(); setHistorial(null); setVerHistorial(false); }, [cargar]);

  const cargarHistorial = async () => {
    try {
      const { data: h } = await axios.get(`${API}/inventory/consumos-diarios`, { ...opts, params: { almacen_id: depositoId } });
      setHistorial(h || []);
    } catch (e) { toast.error(err(e, "No se pudo cargar el historial")); }
  };
  const alternarHistorial = () => { if (!verHistorial && historial === null) cargarHistorial(); setVerHistorial(v => !v); };

  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500 p-4"><Loader2 size={15} className="animate-spin" /> Cargando depósito...</div>;

  const lineas = data.grupos.flatMap(g => g.lineas);
  const conConsumo = lineas.filter(l => Number(consumo[l.id] || 0) > 0);
  const excedidas = lineas.filter(l => Number(consumo[l.id] || 0) > l.cantidad);
  const validar = async () => {
    if (!conConsumo.length) { toast.error("Cargá el consumo en al menos un ítem"); return; }
    if (excedidas.length) { toast.error("El consumo supera lo que hay en el depósito"); return; }
    setBusy("consumo");
    try {
      const { data: a } = await axios.post(`${API}/inventory/depositos/${encodeURIComponent(depositoId)}/consumo`, {
        fecha, notas, lineas: conConsumo.map(l => ({ existencia_id: l.id, cantidad: Number(consumo[l.id]) })),
      }, opts);
      toast.success(`Consumo validado: ${a.numero} (${a.lineas.length} ítem(s))`);
      setNotas(""); setHistorial(null); await cargar(); if (verHistorial) await cargarHistorial(); onChange?.();
    } catch (e) { toast.error(err(e, "No se pudo validar el consumo")); }
    finally { setBusy(""); }
  };
  const confirmarEntrega = async (cc) => {
    const valores = entregas[cc.id] || {};
    const lin = cc.lineas.map(l => ({ idx: l.idx, cantidad: Number(valores[l.idx] ?? l.cantidad) || 0 })).filter(l => l.cantidad > 0);
    if (!lin.length) { toast.error("Indicá lo entregado en al menos un ítem"); return; }
    setBusy(cc.id);
    try {
      await axios.post(`${API}/inventory/depositos/${encodeURIComponent(depositoId)}/caja-chica/${encodeURIComponent(cc.id)}/entrega`, { lineas: lin }, opts);
      toast.success(`${cc.numero}: entrega confirmada`);
      await cargar(); onChange?.();
    } catch (e) { toast.error(err(e, "No se pudo confirmar la entrega")); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="deposito-panel">
      {data.grupos.every(g => g.lineas.length === 0) && (
        <p className="text-sm text-gray-400 italic bg-white border border-gray-200 rounded-xl px-4 py-6 text-center" data-testid="deposito-vacio">El depósito no tiene materiales con saldo.</p>
      )}
      {data.grupos.filter(g => g.lineas.length > 0).map(g => (
        <div key={g.origen} className="bg-white border border-gray-200 rounded-2xl overflow-x-auto" data-testid={`deposito-grupo-${g.origen}`}>
          <p className="px-4 py-2.5 text-sm font-bold text-[#1A4B7A] bg-[#EBF2F9] border-b border-gray-100 flex items-center gap-1.5"><Warehouse size={14} /> {g.titulo} · {g.lineas.length} ítem(s)</p>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase">
              <tr><th className="text-left px-4 py-2">Ítem</th><th className="text-left px-4 py-2">Unidad</th><th className="text-right px-4 py-2">Cant. actual</th>
                <th className="text-right px-4 py-2 bg-amber-50">Consumo</th><th className="text-right px-4 py-2">Saldo</th></tr>
            </thead>
            <tbody>
              {g.lineas.map(l => {
                const c = Number(consumo[l.id] || 0);
                return (
                  <tr key={l.id} className="border-t border-gray-100" data-testid={`deposito-fila-${l.id}`}>
                    <td className="px-4 py-2 font-medium text-gray-900">{l.code && <span className="text-xs font-mono text-gray-400 mr-1.5">{l.code}</span>}{l.nombre}
                      {l.refs?.length > 0 && <span className="block text-[11px] font-normal text-gray-400">{l.refs.join(" · ")}</span>}</td>
                    <td className="px-4 py-2 text-gray-500">{l.unidad}</td>
                    <td className="px-4 py-2 text-right tabular-nums" data-testid={`deposito-cant-${l.id}`}>{num(l.cantidad)}</td>
                    <td className="px-4 py-2 text-right bg-amber-50/40">
                      <input type="number" min="0" step="any" value={consumo[l.id] ?? ""} disabled={!data.puede_consumir}
                        onChange={e => setConsumo(s => ({ ...s, [l.id]: e.target.value }))}
                        className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right" data-testid={`deposito-consumo-${l.id}`} />
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums ${c > l.cantidad ? "text-red-600 font-semibold" : "text-gray-700"}`} data-testid={`deposito-saldo-${l.id}`}>{num(l.cantidad - c)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      {data.puede_consumir && lineas.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-3 flex flex-wrap items-end gap-3">
          <div><label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Fecha</label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="deposito-fecha" /></div>
          <div className="flex-1 min-w-[180px]"><label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Notas</label>
            <input value={notas} onChange={e => setNotas(e.target.value)} placeholder="Opcional" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="deposito-notas" /></div>
          <span className="text-xs text-gray-500">{conConsumo.length} ítem(s) con consumo</span>
          <button onClick={validar} disabled={busy === "consumo"} className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60" data-testid="deposito-validar">
            {busy === "consumo" ? <Loader2 size={15} className="animate-spin" /> : <ClipboardList size={15} />} Validar consumo
          </button>
        </div>
      )}
      {data.pendientes_caja_chica.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 space-y-3" data-testid="deposito-caja-chica-pendiente">
          <p className="text-sm font-semibold text-amber-900">Caja Chica pendiente de entrega en obra ({data.pendientes_caja_chica.length})</p>
          {data.pendientes_caja_chica.map(cc => (
            <div key={cc.id} className="bg-white border border-amber-200 rounded-xl p-3 space-y-2" data-testid={`deposito-cc-${cc.id}`}>
              <p className="text-sm font-semibold text-gray-900">{cc.numero}{cc.proveedor ? ` · ${cc.proveedor}` : ""}{cc.ap_number ? <span className="text-xs font-normal text-gray-500"> · {cc.ap_number}</span> : null}</p>
              {cc.lineas.map(l => (
                <div key={l.idx} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-700">{l.descripcion} <span className="text-xs text-gray-400">(compró {num(l.cantidad)} {l.unidad})</span></span>
                  <input type="number" min="0" step="any" max={l.cantidad} disabled={!data.puede_confirmar}
                    value={(entregas[cc.id] || {})[l.idx] ?? l.cantidad}
                    onChange={e => setEntregas(s => ({ ...s, [cc.id]: { ...(s[cc.id] || {}), [l.idx]: e.target.value } }))}
                    className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right" data-testid={`deposito-cc-cant-${cc.id}-${l.idx}`} />
                </div>
              ))}
              {data.puede_confirmar && (
                <div className="flex justify-end">
                  <button onClick={() => confirmarEntrega(cc)} disabled={busy === cc.id} className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245a8a] text-white px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-60" data-testid={`deposito-cc-confirmar-${cc.id}`}>
                    {busy === cc.id ? <Loader2 size={13} className="animate-spin" /> : <PackageCheck size={13} />} Confirmar entrega en obra
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div>
        <button onClick={alternarHistorial} className="flex items-center gap-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-xs font-semibold" data-testid="deposito-historial-btn">
          <History size={14} /> {verHistorial ? "Ocultar historial de consumo" : "Historial de consumo"}
        </button>
        {verHistorial && (
          <div className="mt-2 space-y-2" data-testid="deposito-historial">
            {historial === null ? <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Cargando...</p>
              : historial.length === 0 ? <p className="text-xs text-gray-400 italic">Todavía no hay consumos validados en este depósito.</p>
              : historial.map(a => (
                <div key={a.id} className="bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm" data-testid={`deposito-acta-${a.numero}`}>
                  <p className="font-semibold text-gray-900">{a.numero} · {a.fecha} <span className="text-xs font-normal text-gray-500">· validado {fechaHora(a.created_at)} por {a.registrado_por_name}</span></p>
                  <ul className="mt-1 text-xs text-gray-600 space-y-0.5">
                    {(a.lineas || []).map((l, i) => (
                      <li key={i}>{l.origen_titulo ? `${l.origen_titulo} · ` : ""}{l.name}: {num(l.consumo)} {l.unit} <span className="text-gray-400">(quedó {num(l.queda)})</span></li>
                    ))}
                  </ul>
                  {a.notas && <p className="text-xs text-gray-400 mt-1">{a.notas}</p>}
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
